/**
 * Fleet.js
 *
 * Phase D (June 2026) — Ship Fleet management at /fleet.
 * Idle garage ships (anything you own that isn't your active ride) can be
 * deployed on timed assignments — Mining Run, Trade Run, Navy Patrol — and
 * keep earning while you play or while you're offline.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { toastRepChanges } from '../lib/factionToast';
import {
  Rocket, Loader2, ArrowLeft, Pickaxe, Coins, ShieldCheck, Timer,
  PackageOpen, Undo2, Ship,
} from 'lucide-react';

const ASSIGNMENT_ICONS = { mining_run: Pickaxe, trade_run: Coins, patrol: ShieldCheck };
const ASSIGNMENT_COLORS = {
  mining_run: 'text-amber-300 border-amber-400/30 bg-amber-500/10',
  trade_run: 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10',
  patrol: 'text-sky-300 border-sky-400/30 bg-sky-500/10',
};

function fmtRemaining(s) {
  if (s <= 0) return 'Ready';
  const m = Math.floor(s / 60); const sec = s % 60;
  return `${m}m ${String(sec).padStart(2, '0')}s`;
}

export default function Fleet() {
  const { api, updateCredits } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/api/fleet');
      setData(d);
    } catch (_e) {
      setData({ assignments: [], deployable: [], missions: [] });
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // 1s countdown tick while missions are running
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const deploy = async (shipId, assignment) => {
    setBusy(`deploy-${shipId}`);
    try {
      const { data: r } = await api.post('/api/fleet/deploy', { ship_id: shipId, assignment });
      toast.success(`${r.mission.ship_name} deployed on ${r.mission.assignment_name}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Deploy failed'); }
    finally { setBusy(''); }
  };

  const collect = async (mission) => {
    setBusy(`collect-${mission.mission_id}`);
    try {
      const { data: r } = await api.post('/api/fleet/collect', { mission_id: mission.mission_id });
      if (r.credits != null) updateCredits?.(r.credits);
      if (r.credits_gained > 0) toast.success(`${mission.ship_name} returned · +${r.credits_gained.toLocaleString()} cr`);
      if (r.ore_summary?.length) toast.success(`${mission.ship_name} hauled in ${r.ore_summary.join(' · ')}`);
      toastRepChanges(r.rep_changes);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Collect failed'); }
    finally { setBusy(''); }
  };

  const recall = async (mission) => {
    setBusy(`recall-${mission.mission_id}`);
    try {
      await api.post('/api/fleet/recall', { mission_id: mission.mission_id });
      toast.info(`${mission.ship_name} recalled — assignment forfeited.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Recall failed'); }
    finally { setBusy(''); }
  };

  const missions = data?.missions || [];
  const deployable = (data?.deployable || []).filter((s) => !s.deployed);
  const assignments = data?.assignments || [];

  return (
    <div className="min-h-screen">
      <Header breadcrumbs={[{ label: 'Fleet' }]} />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Rocket className="w-7 h-7 text-sky-300" />
            <div>
              <h1 className="text-3xl font-bold text-primary" data-testid="fleet-title">Fleet Command</h1>
              <p className="text-sm text-muted-foreground">Put your idle ships to work — they earn even while you're away.</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/explore/octants')} data-testid="fleet-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>

        {data === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-sky-400/60" /></div>
        ) : (
          <div className="space-y-6">
            {/* Active missions */}
            {missions.length > 0 && (
              <div className="rounded-xl border border-sky-400/25 bg-gradient-to-br from-sky-950/25 to-transparent p-5" data-testid="fleet-missions">
                <div className="text-sm font-semibold text-sky-100 mb-3 flex items-center gap-2">
                  <Timer className="w-4 h-4" /> Ships on Assignment
                </div>
                <div className="space-y-2">
                  {missions.map((m) => {
                    const Icon = ASSIGNMENT_ICONS[m.assignment] || Rocket;
                    const endsIn = Math.max(0, Math.floor((new Date(m.ends_at) - Date.now()) / 1000));
                    const ready = endsIn <= 0;
                    return (
                      <div key={m.mission_id} className="flex items-center justify-between gap-3 rounded-lg border border-sky-400/15 bg-black/30 px-4 py-3" data-testid={`fleet-mission-${m.ship_id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <Icon className="w-5 h-5 text-sky-300 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-slate-100 font-medium">{m.ship_name}</div>
                            <div className="text-xs text-slate-400">{m.assignment_name}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className={`text-sm font-mono tabular-nums ${ready ? 'text-emerald-300' : 'text-slate-300'}`} data-testid={`fleet-timer-${m.ship_id}`}>
                            {fmtRemaining(endsIn)}
                          </span>
                          {ready ? (
                            <Button
                              size="sm" onClick={() => collect(m)}
                              disabled={busy === `collect-${m.mission_id}`}
                              className="bg-emerald-500/90 hover:bg-emerald-400 text-slate-900 font-semibold"
                              data-testid={`fleet-collect-${m.ship_id}`}
                            >
                              <PackageOpen className="w-4 h-4 mr-1.5" /> Collect
                            </Button>
                          ) : (
                            <Button
                              size="sm" variant="outline" onClick={() => recall(m)}
                              disabled={busy === `recall-${m.mission_id}`}
                              data-testid={`fleet-recall-${m.ship_id}`}
                            >
                              <Undo2 className="w-4 h-4 mr-1.5" /> Recall
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Assignment catalog */}
            <div className="grid sm:grid-cols-3 gap-3">
              {assignments.map((a) => {
                const Icon = ASSIGNMENT_ICONS[a.id] || Rocket;
                return (
                  <div key={a.id} className={`rounded-xl border p-4 ${ASSIGNMENT_COLORS[a.id] || 'border-slate-700/40'}`}>
                    <div className="flex items-center gap-2 font-semibold text-sm">
                      <Icon className="w-4 h-4" /> {a.name}
                      <span className="ml-auto text-xs font-mono opacity-80">{a.duration_min}m</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5">{a.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* Deployable ships */}
            <div className="rounded-xl border border-slate-600/25 bg-white/[0.02] p-5" data-testid="fleet-deployable">
              <div className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <Ship className="w-4 h-4" /> Idle Ships
              </div>
              {deployable.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">
                  No idle ships. Buy more ships at a station Shipyard — your active ship can't be deployed.
                </p>
              ) : (
                <div className="space-y-3">
                  {deployable.map((s) => (
                    <div key={s.id} className="rounded-lg border border-slate-700/40 bg-black/25 px-4 py-3" data-testid={`fleet-ship-${s.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <div className="text-slate-100 font-medium">{s.name} <span className="text-xs text-slate-500">· {s.klass}</span></div>
                          <div className="text-xs text-slate-400 font-mono mt-0.5">
                            cargo {s.cargo} · hull {s.hull} · shield {s.shield} · spd ×{s.speed_mult}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {assignments.map((a) => {
                            const Icon = ASSIGNMENT_ICONS[a.id] || Rocket;
                            return (
                              <Button
                                key={a.id} size="sm" variant="outline"
                                onClick={() => deploy(s.id, a.id)}
                                disabled={busy === `deploy-${s.id}`}
                                className="text-xs"
                                data-testid={`fleet-deploy-${s.id}-${a.id}`}
                              >
                                <Icon className="w-3.5 h-3.5 mr-1.5" /> {a.name}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
